'use client'

import { useState } from 'react'
import Image from 'next/image'
import moment from 'moment'
import { formatDateLong, formatImageURL, formatPrice } from '@/utils/util'
import StorepayLogo from '@/assets/storepay-long.png'
import PocketZeroLogo from '@/assets/pocketzero.svg'
import LendLogo from '@/assets/lendmn.png'
import CenterModal from './CenterModal'
import { panelFocusRing } from './SidePanel'

export type InstallmentProvider = 'storepay' | 'pocket' | 'lend'

type Plan = { key: string, label: string, description: string, installments: number, days: number }

const evenDates = (plan: Plan) => {
  const today = moment()
  const interval = plan.days / plan.installments
  return Array.from({ length: plan.installments }, (_, index) => index === 0 ? today.toDate() : today.clone().add(index * interval, 'days').toDate())
}

const pocketDates = (plan: Plan) => {
  const today = moment()
  if (plan.installments === 2) return [today.clone().add(15, 'days').toDate(), today.clone().add(plan.days, 'days').toDate()]
  const interval = Math.floor((plan.days - 15) / (plan.installments - 1))
  return Array.from({ length: plan.installments }, (_, index) => {
    if (index === 0) return today.clone().add(15, 'days').toDate()
    if (index === plan.installments - 1) return today.clone().add(plan.days, 'days').toDate()
    return today.clone().add(15 + index * interval, 'days').toDate()
  })
}

const PROVIDERS: Record<InstallmentProvider, { name: string, logo: any, plans: Plan[], dates: (plan: Plan) => Date[], firstIsToday: boolean, notes: string[] }> = {
  storepay: {
    name: 'StorePay',
    logo: StorepayLogo,
    plans: [
      { key: '45days', label: '45 хоног', description: '4 хуваах', installments: 4, days: 45 },
      { key: '60days', label: '60 хоног', description: '3 хуваах', installments: 3, days: 60 },
    ],
    dates: evenDates,
    firstIsToday: true,
    notes: [
      '100,000₮-өөс дээш үнэтэй худалдан авалтад StorePay зээлийн үйлчилгээ ашиглах боломжтой.',
      'Төлбөрийн сонголтыг StorePay гэж сонгоно.',
      'StorePay аппликэйшнээс зээлийн эхний төлбөрийг төлснөөр захиалга баталгаажна.',
    ],
  },
  pocket: {
    name: 'PocketZero',
    logo: PocketZeroLogo,
    plans: [
      { key: '30days', label: '30 хоног', description: '2 хуваах', installments: 2, days: 30 },
      { key: '45days', label: '45 хоног', description: '3 хуваах', installments: 3, days: 45 },
      { key: '60days', label: '60 хоног', description: '4 хуваах', installments: 4, days: 60 },
      { key: '90days', label: '90 хоног', description: '6 хуваах', installments: 6, days: 90 },
    ],
    dates: pocketDates,
    firstIsToday: false,
    notes: [
      '100,000₮-өөс дээш үнэтэй худалдан авалтад Pocket зээлийн үйлчилгээ ашиглах боломжтой.',
      'Төлбөрийн сонголтыг Pocket гэж сонгоно.',
      'Гарч ирсэн QR кодыг Pocket аппликэйшны PocketZero цэсээс уншуулж, төлбөрөө баталгаажуулна.',
    ],
  },
  lend: {
    name: 'LendMN',
    logo: LendLogo,
    plans: [
      { key: '30days', label: '30 хоног', description: '1 хуваах', installments: 1, days: 30 },
      { key: '60days', label: '60 хоног', description: '4 хуваах', installments: 4, days: 60 },
    ],
    dates: evenDates,
    firstIsToday: true,
    notes: [
      '100,000₮-өөс дээш үнэтэй худалдан авалтад Lend зээлийн үйлчилгээ ашиглах боломжтой.',
      'Төлбөрийн сонголтыг LendDy гэж сонгоно.',
      'LendMN аппликэйшнээс зээлийн үүссэн нэхэмжлэлийг зөвшөөрснөөр гүйлгээ амжилттай болно.',
    ],
  },
}

function Ring({ index, total }: { index: number, total: number }) {
  const size = 44
  const radius = (size - 4) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - ((index + 1) / total) * circumference
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={radius} className="stroke-border" strokeWidth="1.5" fill="transparent" />
        <circle cx={size / 2} cy={size / 2} r={radius} className="stroke-foreground transition-[stroke-dashoffset] duration-500" strokeWidth="1.5" fill="transparent" strokeDasharray={circumference} strokeDashoffset={offset} />
      </svg>
      <span className="absolute inset-0 grid place-items-center text-sm tabular-nums">{index + 1}</span>
    </div>
  )
}

export default function InstallmentModal({ open, onClose, provider, productPrice, productName, productImage }: { open: boolean, onClose: () => void, provider: InstallmentProvider, productPrice: number, productName: string, productImage: string }) {
  const config = PROVIDERS[provider]
  const [selected, setSelected] = useState<Record<InstallmentProvider, string>>({ storepay: '45days', pocket: '30days', lend: '30days' })
  const plan = config.plans.find(p => p.key === selected[provider]) ?? config.plans[0]
  const dates = config.dates(plan)
  const perPayment = Math.ceil(productPrice / plan.installments)

  const paymentLabel = (index: number) => {
    if (index === 0) return 'Эхний төлөлт'
    if (index === plan.installments - 1) return 'Сүүлийн төлөлт'
    return `${index + 1} дэх төлөлт`
  }

  return (
    <CenterModal
      open={open}
      onClose={onClose}
      title={<Image src={config.logo} alt={config.name} className="h-5 w-auto object-contain" />}
      footer={
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className={`h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 ${panelFocusRing}`}>Ойлголоо</button>
        </div>
      }
    >
      <div className="flex items-center gap-4">
        <div className="w-14 aspect-3/4 bg-muted overflow-hidden shrink-0">
          <img src={formatImageURL(productImage)} alt={productName} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <div className="text-sm line-clamp-2">{productName}</div>
          <div className="mt-1 text-sm tabular-nums text-muted-foreground">{formatPrice(productPrice)}</div>
        </div>
      </div>

      {config.plans.length > 1 && (
        <div role="tablist" aria-label="Хуваан төлөх нөхцөл" className="mt-8 flex items-center gap-6 border-b border-border overflow-x-auto scrollbar-hide">
          {config.plans.map(p => (
            <button key={p.key} type="button" role="tab" aria-selected={p.key === plan.key} onClick={() => setSelected(s => ({ ...s, [provider]: p.key }))} className={`shrink-0 -mb-px py-3 border-b text-[13px] whitespace-nowrap transition-colors duration-150 ${p.key === plan.key ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'} ${panelFocusRing}`}>
              {p.label} · {p.description}
            </button>
          ))}
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 sm:flex gap-x-4 gap-y-8">
        {dates.map((date, index) => {
          const isLast = index === plan.installments - 1
          const amount = isLast ? productPrice - perPayment * (plan.installments - 1) : perPayment
          return (
            <div key={index} className="sm:flex-1 min-w-0 flex flex-col items-center text-center">
              <Ring index={index} total={plan.installments} />
              <div className="mt-3 text-[13px]">{paymentLabel(index)}</div>
              <div className="mt-1 text-sm tabular-nums">{formatPrice(amount)}</div>
              <div className="mt-1 text-xs text-muted-foreground tabular-nums">{index === 0 && config.firstIsToday ? 'Өнөөдөр' : formatDateLong(date)}</div>
            </div>
          )
        })}
      </div>

      <div className="mt-10 pt-6 border-t border-border space-y-2 text-[13px] leading-relaxed text-muted-foreground">
        <p>*Дээрх төлөлтүүд нийт үнийн дүнг хуваасан болно.</p>
        {config.notes.map(note => <p key={note}>*{note}</p>)}
      </div>
    </CenterModal>
  )
}
